/**
 * Modal for copying selected documents to another loan
 * Shows selected files summary and destination loan picker
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Loader2, FileText, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { LoanAutocomplete, type EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import { copyDocumentsBetweenLoans, getCopyResultMessage } from '@/services/encompassDocumentCopyService';
import { formatFileSize } from '@/services/encompassDocumentService';
import type { EncompassAttachment } from '@/types/encompass-documents';

interface CopyDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAttachments: EncompassAttachment[];
  sourceLoanId: string;
  organizationId: string;
  onCopyComplete: () => void;
  /** Callback to update checked attachments (for trimming after partial success) */
  onUpdateSelection?: (newSelection: Set<string>) => void;
}

export function CopyDocumentsModal({
  isOpen,
  onClose,
  selectedAttachments,
  sourceLoanId,
  organizationId,
  onCopyComplete,
  onUpdateSelection
}: CopyDocumentsModalProps) {
  const [destinationLoan, setDestinationLoan] = useState<EncompassLoan | null>(null);
  const [copying, setCopying] = useState(false);
  // Phase 5: Track detailed errors from copy operation
  const [copyErrors, setCopyErrors] = useState<string[]>([]);
  // Phase 9: Track elapsed time during copy operation
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Phase 9: Increment elapsed time counter while copying
  useEffect(() => {
    if (!copying) {
      setElapsedSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsedSeconds(s => s + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [copying]);

  // Calculate total size of selected files
  const totalSize = useMemo(() => {
    return selectedAttachments.reduce((sum, att) => sum + (att.file_size || 0), 0);
  }, [selectedAttachments]);

  // Get borrower name for display
  const getBorrowerName = (loan: EncompassLoan) => {
    const firstName = loan.borrower_first_name || '';
    const lastName = loan.borrower_last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  };

  const handleCopy = async () => {
    if (!destinationLoan) {
return;
}

    setCopying(true);
    setCopyErrors([]); // Clear previous errors

    try {
      const result = await copyDocumentsBetweenLoans({
        attachmentIds: selectedAttachments.map(a => a.id),
        destinationLoanId: destinationLoan.encompass_loan_id,
        sourceLoanId
      });

      const message = getCopyResultMessage(result, getBorrowerName(destinationLoan));

      // Phase 5: Store detailed errors for display
      if (result.errors && result.errors.length > 0) {
        setCopyErrors(result.errors);
      }

      if (result.success) {
        toast.success(message);
        onCopyComplete();
        handleClose();
      } else if (result.copiedCount > 0) {
        // Partial success - trim selection to only failed files so retry doesn't re-copy successful ones
        toast.warning(message);

        // If we have newAttachmentIds, we know which files succeeded (by count match)
        // Note: This relies on edge function returning newAttachmentIds for successful copies
        if (onUpdateSelection && result.newAttachmentIds && result.newAttachmentIds.length > 0) {
          // Since edge function processes in order, first N attachments succeeded
          // where N = result.copiedCount = result.newAttachmentIds.length
          const successfulCount = result.newAttachmentIds.length;
          const attachmentIds = selectedAttachments.map(a => a.id);
          const failedIds = attachmentIds.slice(successfulCount);

          if (failedIds.length > 0) {
            onUpdateSelection(new Set(failedIds));
          }
        }
        // Don't close modal so user can see which files failed and retry
      } else {
        // Complete failure - prefer the specific error (e.g. a 546 resource
        // limit) over the generic summary; keep modal open so files are visible.
        toast.error(result.errors?.[0] || message);
      }
    } catch (error) {
      // Phase 5: Show specific error message, not generic
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setCopyErrors([errorMessage]);
      toast.error(errorMessage);
    } finally {
      setCopying(false);
    }
  };

  const handleClose = () => {
    if (!copying) {
      setDestinationLoan(null);
      setCopyErrors([]); // Clear errors on close
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {/*
        Modal Structure (based on shadcn/ui best practices):
        - max-h-[85vh]: Constrain to 85% of viewport height
        - flex flex-col: Flexbox column for header/body/footer layout
        - p-0 gap-0: Remove default padding, control per section
      */}
      <DialogContent className="max-w-2xl w-full max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">

        {/* Fixed Header - flex-shrink-0 keeps it pinned */}
        <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b bg-background">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Copy {selectedAttachments.length} Document{selectedAttachments.length !== 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Select a destination loan. Documents will appear in "Unassigned Documents" folder.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable Body - flex-1 + min-h-0 + overflow-y-auto enables scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">

            {/* Selected files list */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Selected Files ({formatFileSize(totalSize)} total)
              </Label>
              <div className="rounded-lg border bg-card overflow-hidden">
                <div className="max-h-56 overflow-y-auto">
                  {selectedAttachments.map((att, index) => (
                    <div
                      key={att.id}
                      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-accent/50 transition-colors ${
                        index !== selectedAttachments.length - 1 ? 'border-b border-border' : ''
                      }`}
                    >
                      <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <span className="truncate flex-1 min-w-0 text-sm">{att.title || 'Untitled'}</span>
                      <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums font-mono">
                        {formatFileSize(att.file_size)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Destination loan selector */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Destination Loan
              </Label>
              <LoanAutocomplete
                organizationId={organizationId}
                onSelect={setDestinationLoan}
                selectedLoan={destinationLoan}
                onClear={() => setDestinationLoan(null)}
                excludeLoanId={sourceLoanId}
                allChannels={true}
              />
            </div>

            {/* Destination preview */}
            {destinationLoan && (
              <div className="p-4 bg-muted rounded-lg border">
                <div className="text-base font-semibold">{getBorrowerName(destinationLoan)}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Loan #{destinationLoan.trans_details_loan_number || 'N/A'}
                  {destinationLoan.trans_details_loan_amt && (
                    <span className="ml-2">• ${destinationLoan.trans_details_loan_amt.toLocaleString()}</span>
                  )}
                </div>
                {destinationLoan.milestone && (
                  <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    {destinationLoan.milestone}
                  </div>
                )}
              </div>
            )}

            {/* Warning for large batches */}
            {selectedAttachments.length > 10 && (
              <Alert className="bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  Copying {selectedAttachments.length} files may take a moment.
                </AlertDescription>
              </Alert>
            )}

            {/* Phase 5: Display detailed error messages */}
            {copyErrors.length > 0 && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <h4 className="font-medium text-destructive mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {copyErrors.length === 1 ? 'Error' : `${copyErrors.length} Errors`}
                </h4>
                <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                  {copyErrors.map((error, i) => (
                    <li key={i} className="text-destructive/80 text-xs">
                      • {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Footer - flex-shrink-0 keeps it pinned */}
        <div className="flex-shrink-0 px-6 py-4 border-t bg-background">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={copying}>
              Cancel
            </Button>
            <Button
              onClick={handleCopy}
              disabled={!destinationLoan}
              className={copying ? "pointer-events-none" : ""}
            >
              {copying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Copying {selectedAttachments.length} files... ({elapsedSeconds}s)
                </>
              ) : (
                'Copy Files'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
